/**
 * Detalhe de uma solicitação (somente consulta — página do colaborador).
 * Sem botões de autorizar/atender: essas ações vivem na página de gestão.
 * Única ação possível: cancelar a própria solicitação enquanto ABERTA.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { FiFileText } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import {
  cancelarSolicitacao, editarFotoSolicitacao, marcarNovidadeVista, obterAssistencia,
  obterRequisicao, PERMS_GESTAO_SOLICITACOES, restaurarFotoSolicitacao,
  SETORES_ASP, SITUACAO_LABELS, SITUACAO_TONES,
} from "../../../services/solicitacoes";
import { PageContainer, Section, SectionTitle } from "../../../styles/shared";
import { fmtQtd } from "../helpers";
import ImagemViewer from "../../../components/ImagemViewer";
import { cabecalhoDaFoto } from "./imprimirImagens";
import {
  descreverFitas, formatarDataHora, formatarMedidas, tipoPorValor, unwrapItem,
} from "./utils";
import {
  BlocoDados, Card, ErroBox, FotosWrap, FotoThumbBox, PecaTitulo, TimelineItem, TimelinePonto, TimelineTexto,
  TimelineWrap, VerComprovanteBtn,
} from "./styles";
import { Carregando } from "../_shared/Estados";
import ConversaSolicitacao from "../_shared/ConversaSolicitacao";

/**
 * Troca UMA foto no detalhe já carregado, sem rebaixar o payload inteiro.
 * O detalhe traz todas as fotos em base64; recarregar a cada giro custava
 * centenas de KB no 3G da obra e ainda fazia o usuário esperar para ver o
 * resultado do próprio giro.
 */
function trocarFotoNoDetalhe(detalhe, fotoId, imagem, editada) {
  if (!detalhe) return detalhe;
  const trocar = (fotos) =>
    (fotos || []).map((f) => (f.id === fotoId ? { ...f, imagem, editada } : f));
  return {
    ...detalhe,
    fotos: detalhe.fotos ? trocar(detalhe.fotos) : detalhe.fotos,
    pecas: detalhe.pecas
      ? detalhe.pecas.map((p) => ({ ...p, fotos: trocar(p.fotos) }))
      : detalhe.pecas,
  };
}

const FAMILIAS_VALIDAS = ["assistencia", "requisicao"];

function montarTimeline(s) {
  const passos = [
    {
      chave: "criada",
      titulo: "Criada",
      tone: "info",
      quem: s.criado_por_nome,
      quando: s.created_at,
    },
  ];
  if (s.situacao === "RECUSADA") {
    passos.push({
      chave: "recusada",
      titulo: "Recusada",
      tone: "danger",
      quem: s.autorizado_por_nome,
      quando: s.recusada_em,
      motivo: s.motivo_recusa,
    });
    return passos;
  }
  if (s.situacao === "JUNTADA") {
    passos.push({
      chave: "juntada",
      titulo: `Juntada na ${s.juntada_na_numero}`,
      tone: "neutral",
      quem: s.juntada_por_nome,
      quando: s.juntada_em,
    });
    return passos;
  }
  if (s.situacao === "CANCELADA") {
    passos.push({
      chave: "cancelada",
      titulo: "Cancelada",
      tone: "neutral",
      quando: s.cancelada_em,
      motivo: s.motivo_cancelamento,
    });
    return passos;
  }
  if (
    s.autorizada_em ||
    ["AUTORIZADA", "EM_ANDAMENTO", "ATENDIDA"].includes(s.situacao)
  ) {
    passos.push({
      chave: "autorizada",
      titulo: "Autorizada",
      tone: "info",
      quem: s.autorizado_por_nome,
      quando: s.autorizada_em,
    });
  }
  if (s.em_andamento_em || s.situacao === "EM_ANDAMENTO") {
    passos.push({
      chave: "em_andamento",
      titulo: "Em andamento",
      tone: "info",
      quem: s.iniciado_por_nome,
      quando: s.em_andamento_em,
    });
  }
  // Marcos da expedição (manuais/assinados): só aparecem quando existem.
  if (s.pronta_em) {
    passos.push({
      chave: "pronta",
      titulo: "Pronta para retirada",
      tone: "info",
      quem: s.pronta_por_nome,
      quando: s.pronta_em,
    });
  }
  if (s.expedida_em) {
    passos.push({
      chave: "expedida",
      titulo: "Expedida",
      tone: "success",
      quem: (s.retiradas || []).find((r) => r.completa)?.retirado_por_nome
        ? `retirada por ${(s.retiradas || []).find((r) => r.completa).retirado_por_nome}`
        : undefined,
      quando: s.expedida_em,
    });
  }
  if (s.atendida_em || s.situacao === "ATENDIDA") {
    passos.push({
      chave: "atendida",
      titulo: "Atendida",
      tone: "success",
      quem: s.atendido_por_nome,
      quando: s.atendida_em,
    });
  }
  return passos;
}

// `peca` acompanha a foto para o visualizador montar o cabecalho de
// impressao. Em RQA/RQI a foto e da solicitacao e vem sem peca.
function GaleriaFotos({ fotos, peca = null, aoAbrir }) {
  if (!fotos?.length) return null;
  return (
    <FotosWrap>
      {fotos.map((f) => (
        <FotoThumbBox
          key={f.id || f.imagem.slice(-24)}
          as="button"
          type="button"
          style={{ cursor: "pointer", padding: 0 }}
          onClick={() => aoAbrir({ foto: f, peca })}
        >
          <img src={f.imagem} alt={f.legenda || "Foto da solicitação"} />
        </FotoThumbBox>
      ))}
    </FotosWrap>
  );
}

export default function Detalhe() {
  const { familia, id } = useParams();
  const navigate = useNavigate();
  const { request, usuario, hasAnyPermission } = useAuth();
  const toast = useToast();

  const [solicitacao, setSolicitacao] = useState(null);
  const [erro, setErro] = useState("");
  const [fotoAberta, setFotoAberta] = useState(null);

  const familiaValida = FAMILIAS_VALIDAS.includes(familia);

  const carregar = useCallback(async () => {
    setErro("");
    try {
      const obter = familia === "requisicao" ? obterRequisicao : obterAssistencia;
      const payload = await obter(request, id);
      const dado = unwrapItem(payload);
      setSolicitacao(dado);
      // Quem pediu abriu: some o aviso de "autorizada/recusada/atendida" do
      // sino e o ponto no Hub (PM-07). Só vale para o criador (o backend confere).
      if (dado?.tem_novidade) marcarNovidadeVista(request, familia, id).catch(() => {});
    } catch (e) {
      setErro(e?.message || "Não foi possível carregar a solicitação.");
    }
  }, [familia, id, request]);

  useEffect(() => {
    if (familiaValida) carregar();
  }, [carregar, familiaValida]);

  // Girar/cortar salvam sozinhos; recarrega para o estado (inclusive o
  // marcador `editada`) vir do servidor. Se o POST falhar, o visualizador
  // mantem a imagem antiga na tela.
  const aplicarFoto = (fotoId, imagem, editada) => {
    setSolicitacao((d) => trocarFotoNoDetalhe(d, fotoId, imagem, editada));
    setFotoAberta((atual) =>
      atual && atual.foto.id === fotoId
        ? { ...atual, foto: { ...atual.foto, imagem, editada } }
        : atual);
  };

  const salvarFoto = async (imagem) => {
    const r = await editarFotoSolicitacao(
      request, familia, id, fotoAberta.foto.id, imagem,
    );
    aplicarFoto(fotoAberta.foto.id, imagem, r?.editada ?? true);
  };

  const restaurarFoto = async () => {
    const r = await restaurarFotoSolicitacao(
      request, familia, id, fotoAberta.foto.id,
    );
    if (r?.imagem) aplicarFoto(fotoAberta.foto.id, r.imagem, false);
    return r?.imagem;
  };

  if (!familiaValida) return <Navigate to=".." replace />;

  const s = solicitacao;
  const tipoInfo = s ? tipoPorValor(s.tipo) : null;
  const ehGestor = hasAnyPermission(PERMS_GESTAO_SOLICITACOES);
  // Sem gestão, o backend só devolve as próprias solicitações; para gestor
  // (que enxerga todas), compara o nome do criador antes de ofertar cancelar.
  const podeCancelar =
    s?.situacao === "ABERTA" && (!ehGestor || s?.criado_por_nome === usuario?.nome);

  const cancelar = async () => {
    const motivo = window.prompt("Motivo do cancelamento (opcional):");
    if (motivo === null) return; // desistiu — sem toast de sucesso
    await cancelarSolicitacao(request, familia, id, motivo.trim());
    toast.success("Solicitação cancelada.");
    await carregar();
  };

  const rotuloSetor = (valor) =>
    SETORES_ASP.find((op) => op.value === valor)?.label || valor;

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => navigate("..")}
        title={s?.numero || "Solicitação"}
        subtitle={tipoInfo ? `${tipoInfo.label} (${tipoInfo.value})` : ""}
        badges={
          s
            ? [{ label: SITUACAO_LABELS[s.situacao] || s.situacao, tone: SITUACAO_TONES[s.situacao] || "neutral" }]
            : []
        }
      />

      {erro && <ErroBox>{erro}</ErroBox>}
      {!erro && !s && <Carregando />}

      {s && (
        <>
          <Card>
            <BlocoDados>
              <div><dt>Pedido</dt><dd>{s.num_pedido || "—"}</dd></div>
              <div><dt>Cliente</dt><dd>{s.cliente_nome || "—"}</dd></div>
              {s.supervisor_nome && (
                <div><dt>Supervisor</dt><dd>{s.supervisor_nome}</dd></div>
              )}
              {s.lider_montagem_nome && (
                <div><dt>Líder de montagem</dt><dd>{s.lider_montagem_nome}</dd></div>
              )}
              {s.setor && <div><dt>Setor</dt><dd>{rotuloSetor(s.setor)}</dd></div>}
              {s.lote_producao && (
                <div><dt>Lote de produção</dt><dd>{s.lote_producao}</dd></div>
              )}
              <div><dt>Criada por</dt><dd>{s.criado_por_nome || "—"}</dd></div>
              <div><dt>Criada em</dt><dd>{formatarDataHora(s.created_at)}</dd></div>
            </BlocoDados>
          </Card>

          {s.situacao === "JUNTADA" && s.juntada_na && (
            <Card style={{ marginTop: 10 }}>
              Esta solicitação foi juntada na <strong>{s.juntada_na_numero}</strong>: as peças, os itens
              e a conversa seguem lá.{" "}
              <button
                type="button"
                onClick={() => navigate(`/suprimentos/solicitacoes/${familia}/${s.juntada_na}`)}
                style={{
                  background: "none", border: 0, padding: 0, color: "var(--accent)",
                  textDecoration: "underline", cursor: "pointer", font: "inherit",
                }}
              >
                Abrir a {s.juntada_na_numero}
              </button>
            </Card>
          )}

          {/* Reabrir o comprovante a qualquer momento — o usuário não fica mais
              preso a salvar o JPG na tela de envio. Caminho ABSOLUTO de propósito:
              a shell é um splat route (/suprimentos/solicitacoes/*) e o
              `navigate("../comprovante")` relativo é não-determinístico no
              React Router v6 (aviso v7_relativeSplatPath) — troca a URL mas não
              re-casa a rota. Absoluto casa a rota certa sempre. */}
          <VerComprovanteBtn
            type="button"
            onClick={() =>
              navigate("/suprimentos/solicitacoes/comprovante", {
                state: { solicitacao: s, familia, tipo: s.tipo, reView: true },
              })
            }
          >
            <FiFileText size={18} /> Visualizar comprovante
          </VerComprovanteBtn>

          {familia === "assistencia" && (
            <Section>
              <SectionTitle>Peças</SectionTitle>
              {(s.pecas || []).map((p, i) => (
                <Card key={p.id || i} style={{ marginBottom: 10 }}>
                  <PecaTitulo>
                    Peça {i + 1} — {p.descricao}
                  </PecaTitulo>
                  <BlocoDados>
                    <div><dt>Ambiente</dt><dd>{p.ambiente || "—"}</dd></div>
                    {p.modulo && <div><dt>Módulo</dt><dd>{p.modulo}{p.ref_modulo ? ` (${p.ref_modulo})` : ""}</dd></div>}
                    <div>
                      <dt>Natureza</dt>
                      <dd>{p.natureza === "NOVA" ? "Peça nova" : "Só mão de obra"}</dd>
                    </div>
                    {(p.material_nome || p.material_codigo) && (
                      <div>
                        <dt>Material</dt>
                        <dd>{[p.material_codigo, p.material_nome].filter(Boolean).join(" - ")}</dd>
                      </div>
                    )}
                    {formatarMedidas(p) && (
                      <div><dt>Medidas</dt><dd>{formatarMedidas(p)}</dd></div>
                    )}
                    <div><dt>Quantidade</dt><dd>{p.quantidade}x</dd></div>
                    {p.acabamento && <div><dt>Acabamento</dt><dd>{p.acabamento}</dd></div>}
                    {p.observacao && <div><dt>Observação</dt><dd>{p.observacao}</dd></div>}
                    {descreverFitas(p) && (
                      <div><dt>Fita de borda</dt><dd>{descreverFitas(p)}</dd></div>
                    )}
                    {p.usinagem && (
                      <div><dt>Usinagem</dt><dd>{p.usinagem_descricao || "Sim"}</dd></div>
                    )}
                    {p.pintura && (
                      <div><dt>Pintura</dt><dd>{p.pintura_descricao || "Sim"}</dd></div>
                    )}
                    <div>
                      <dt>Motivo</dt>
                      <dd>
                        {p.motivo_categoria_display || p.motivo_categoria}
                        {p.motivo_detalhe ? ` — ${p.motivo_detalhe}` : ""}
                      </dd>
                    </div>
                    {p.lote_numero && (
                      <div><dt>Lote MDF</dt><dd>{p.lote_numero}</dd></div>
                    )}
                  </BlocoDados>
                  <GaleriaFotos fotos={p.fotos} peca={p} aoAbrir={setFotoAberta} />
                </Card>
              ))}
            </Section>
          )}

          {familia === "requisicao" && (
            <Section>
              <SectionTitle>Itens</SectionTitle>
              {(s.itens || []).map((it, i) => (
                <Card key={it.id || i} style={{ marginBottom: 10 }}>
                  <PecaTitulo>
                    Item {i + 1} — {it.descricao}
                  </PecaTitulo>
                  <BlocoDados>
                    {it.codigo && <div><dt>Código</dt><dd>{it.codigo}</dd></div>}
                    {it.ambiente && <div><dt>Ambiente</dt><dd>{it.ambiente}</dd></div>}
                    {it.medida && <div><dt>Medida</dt><dd>{it.medida}</dd></div>}
                    <div>
                      <dt>Quantidade</dt>
                      <dd>{fmtQtd(it.quantidade)} {it.unidade_medida || ""}</dd>
                    </div>
                    {it.motivo && (
                      <div>
                        <dt>Motivo</dt>
                        <dd>
                          {it.motivo_display || it.motivo}
                          {it.motivo_detalhe ? ` — ${it.motivo_detalhe}` : ""}
                        </dd>
                      </div>
                    )}
                  </BlocoDados>
                </Card>
              ))}
              {s.fotos?.length > 0 && (
                <Card>
                  <SectionTitle>Fotos</SectionTitle>
                  <GaleriaFotos fotos={s.fotos} aoAbrir={setFotoAberta} />
                </Card>
              )}
            </Section>
          )}

          <Section>
            <SectionTitle>Linha do tempo</SectionTitle>
            <Card>
              <TimelineWrap>
                {montarTimeline(s).map((passo) => (
                  <TimelineItem key={passo.chave}>
                    <TimelinePonto $tone={passo.tone} />
                    <TimelineTexto>
                      <strong>{passo.titulo}</strong>
                      {(passo.quem || passo.quando) && (
                        <small>
                          {[passo.quem, formatarDataHora(passo.quando)]
                            .filter(Boolean)
                            .join(" · ")}
                        </small>
                      )}
                      {passo.motivo && <small>Motivo: {passo.motivo}</small>}
                    </TimelineTexto>
                  </TimelineItem>
                ))}
              </TimelineWrap>
            </Card>
          </Section>

          {podeCancelar && (
            <AsyncButton
              variant="danger"
              onClick={cancelar}
              style={{ width: "100%", minHeight: 52, fontSize: 16, justifyContent: "center" }}
            >
              Cancelar solicitação
            </AsyncButton>
          )}

          {/* Dúvidas com a gestão ficam registradas aqui (PM-12). */}
          {s.situacao !== "JUNTADA" && <ConversaSolicitacao familia={familia} id={id} />}
        </>
      )}

      {fotoAberta && (
        <ImagemViewer
          foto={fotoAberta.foto}
          cabecalho={cabecalhoDaFoto(s, fotoAberta.peca)}
          onFechar={() => setFotoAberta(null)}
          onSalvar={salvarFoto}
          onRestaurar={restaurarFoto}
        />
      )}
    </PageContainer>
  );
}
